import React, { memo, ReactNode } from 'react';
import { TableCell, TableRow } from '../../../components/organisms/Table';
import { SharedDirectory, SharedFile, SharedFilesDataRowEntities } from '../SharedFiles.types';
import { Icons } from '../../../components/atoms/SVG';
import { SharedFilesItemAction } from './SharedFilesItemAction';
import { Hyperlink } from '../../../components/atoms/Hyperlink';
import {
  DownloadByteUnit,
  DownloadFileIcon,
  DownloadFileLink,
  FileIcon,
  getFileExtensionDescription,
} from '../../../_lib/components/Download';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { ISonURLDeleted } from '../../../types/IUrl';
import { DateFormat, DateFormats } from '../../../_lib/components/DateFormat';
import { DownloadAbbr } from '../../../_lib/components/Download/partials/DownloadAbbr';
import { SharedFilesRowCheckbox } from './SharedFilesCheckboxes';

type CreateSharedFilesRow = {
  name: string;
  fileLink: ReactNode;
  action?: ReactNode;
  editDate?: string;
  editedBy?: ISonURLDeleted;
  fileType?: ReactNode;
  fileSizeBytes?: ReactNode;
} & SharedFilesDataRowEntities;

/**
 * Shared Files common Row Component.
 */
function SharedFilesTableRow({
  name,
  fileLink,
  fileType,
  fileSizeBytes,
  editDate,
  editedBy,
  action,
  dataSharedEntityType,
  dataSharedEntityId,
}: CreateSharedFilesRow) {
  const { t } = useSonTranslation('SharedFiles');

  return (
    <TableRow
      className="flexible-tables__tablerow"
    >
      <TableCell customTableDataClass="flexible-tables__tabledata">
        <SharedFilesRowCheckbox
          name={name}
          dataSharedEntityId={dataSharedEntityId}
          dataSharedEntityType={dataSharedEntityType}
        />
      </TableCell>
      <TableCell dataHead={t('table.head.name')} customTableDataClass="flexible-tables__tabledata">
        {fileLink}
      </TableCell>
      <TableCell dataHead={t('table.head.fileType')} customTableDataClass="flexible-tables__tabledata">
        {fileType}
      </TableCell>
      <TableCell dataHead={t('table.head.fileSize')} customTableDataClass="flexible-tables__tabledata">
        {fileSizeBytes}
      </TableCell>
      <TableCell dataHead={t('table.head.editDate')} customTableDataClass="flexible-tables__tabledata">
        {editDate && <DateFormat date={editDate} format={DateFormats.DATE_TIME} />}
      </TableCell>
      <TableCell dataHead={t('table.head.editedBy')} customTableDataClass="flexible-tables__tabledata">
        {editedBy && !editedBy.deletedUser && (
          <Hyperlink className="global--anchor" to={editedBy.url}>
            {editedBy.name}
          </Hyperlink>
        )}
      </TableCell>
      <TableCell dataHead={t('table.head.action')} customTableDataClass="flexible-tables__tabledata">
        <div className="flexible-tables__action-container">{action}</div>
      </TableCell>
    </TableRow>
  );
}

/**
 * Row displaying information about a single file.
 * use memoized Component to avoid re-rendering on sort or checkbox changes.
 */
export const SharedFilesTableRowFile = memo(function ({ file }: { file: SharedFile }) {
  const name = file.fileTitle ? `${file.fileTitle} / ${file.fileName}` : file.fileName;
  return (
    <SharedFilesTableRow
      name={name}
      fileLink={
        <DownloadFileLink download data-shared-files-focusable={file.id} href={file.url} id={file.id}>
          <DownloadFileIcon fileExtension={file.fileExtension} modifier="relative" />
          {file.fileTitle ? `${file.fileTitle} / ${file.fileName}` : file.fileName}
        </DownloadFileLink>
      }
      fileType={
        <DownloadAbbr text={file.fileExtension.toUpperCase()} title={getFileExtensionDescription(file.fileExtension)} />
      }
      fileSizeBytes={<DownloadByteUnit bytes={file.fileSizeBytes} />}
      editDate={file.editDate}
      editedBy={file.editedBy}
      action={<SharedFilesItemAction file={file} />}
      dataSharedEntityId={file.id}
      dataSharedEntityType="file"
    />
  );
});
SharedFilesTableRowFile.displayName = 'SharedFilesTableRowFile';

/**
 * Row displaying information about a directory.
 * use memoized Component to avoid re-rendering on sort or checkbox changes.
 */
export const SharedFilesTableRowDirectory = memo(function ({ dir }: { dir: SharedDirectory }) {
  const { t } = useSonTranslation('SharedFiles');
  return (
    <SharedFilesTableRow
      fileType={t('folder')}
      name={dir.pageLink.name}
      fileLink={
        <DownloadFileLink data-shared-files-focusable="" href={dir.pageLink.url}>
          <FileIcon svgSprite={Icons.FOLDER_OPEN} modifier="relative" />
          {dir.pageLink.name}
        </DownloadFileLink>
      }
      editDate={dir.editDate}
      editedBy={dir.editedBy}
      dataSharedEntityId={dir.pageId}
      dataSharedEntityType="directory"
    />
  );
});
SharedFilesTableRowDirectory.displayName = 'SharedFilesTableRowDirectory';
